import {useCallback, useEffect, useMemo, useState} from "react";
import {AccountApi} from "account/AccountApi";
import type {Plan} from "../gating";
import {reportMomentEligible, upgradeMomentArm} from "upsell/upgradeMomentAdapters";
import {
	momentHistoryStorage, readMomentHistory, rememberMomentShown, rememberNotNow, upgradeMomentVisit,
} from "upsell/upgradeMomentHistory";
import {upgradeMomentFor, type UpgradeMomentModel, type UpgradeSignals} from "upsell/upgradeMoments";

export interface UpgradeMomentView {
	/** The moment to show in the upsell card, or null: none this visit, the control half, or dismissed. */
	model: UpgradeMomentModel | null;
	/** The card came out: the moment's frequency rules start counting from now. */
	rememberShown: () => void;
	/** "Not now": the card goes for this visit, and the big-files moment counts the tap. */
	notNow: () => void;
}

export interface UpgradeMomentInput {
	plan: Plan;
	signedIn: boolean;
	/** The signed-in account the history is kept for; null for a guest, who is never asked. */
	accountId: string | null;
	selection: {files: number; overLimit: boolean};
}

/**
 * Thin adapter for the send screen's upgrade moments (kitecorp/transferify-ui#2909): it reads the
 * account's signals from `GetUsage`, asks `upsell/upgradeMoments` which moment this visit
 * carries, and holds the answer. Every rule lives in the React-free modules it calls.
 *
 * <b>Decided once per visit, in both halves of the test.</b> The first moment the rule returns claims
 * the visit (`upgradeMomentVisit`), is reported as eligible with its half, and is kept: the control half
 * sees nothing, and no second moment is offered on the same visit, whether this panel stays mounted or
 * is mounted again after a send. Signed-in senders only: every signal is the account's own usage.
 */
export function useUpgradeMoment({plan, signedIn, accountId, selection}: UpgradeMomentInput): UpgradeMomentView {
	const [signals, setSignals] = useState<UpgradeSignals | null>(null);
	const [chosen, setChosen] = useState<UpgradeMomentModel | null>(null);
	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		if (!signedIn) return undefined;
		let cancelled = false;
		AccountApi.getUsage()
			.then(usage => {
				if (!cancelled) setSignals({...usage.signals, repeatRecipient: usage.signals.repeatRecipient ?? null,
					unpaidLimit: usage.signals.unpaidLimit ?? null});
			})
			// No signals, no moment: a prompt is never worth an error on the send screen.
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [signedIn, accountId]);

	const candidate = useMemo(() => {
		if (!signedIn || accountId === null || signals === null) return null;
		return upgradeMomentFor({plan, signals, selection,
			history: readMomentHistory(momentHistoryStorage(), accountId), now: Date.now()});
	}, [plan, signedIn, accountId, signals, selection.files, selection.overLimit]);

	useEffect(() => {
		if (chosen !== null || candidate === null || !upgradeMomentVisit.claim()) return;
		const arm = upgradeMomentArm(candidate.moment);
		reportMomentEligible(candidate.moment, arm);
		if (arm === "prompt") setChosen(candidate);
	}, [candidate, chosen]);

	const rememberShown = useCallback(() => {
		if (chosen !== null && accountId !== null) rememberMomentShown(momentHistoryStorage(), accountId, chosen, Date.now());
	}, [chosen, accountId]);
	const notNow = useCallback(() => {
		if (chosen !== null && accountId !== null) rememberNotNow(momentHistoryStorage(), accountId, chosen, Date.now());
		setDismissed(true);
	}, [chosen, accountId]);

	return {model: dismissed ? null : chosen, rememberShown, notNow};
}
